import React, {useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import {Link as RouterLink} from 'react-router-dom';
import {useTranslation, Trans} from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Box,
  Link,
  Snackbar,
  Alert,
} from '@mui/material';
import {fetchPostAuthSafe} from '../app/utils';
import {
  PASSKEY_UI_ENABLED,
  isWebAuthnAvailable,
  isPlatformAuthenticatorAvailable,
  isPasskeyNudgeDismissed,
  isPasskeyNudgeEnrolled,
  setPasskeyNudgeDismissed,
  markPasskeyPresentOnThisDevice,
  listPasskeys,
  registerPasskey,
  getDefaultPasskeyNickname,
  readPasskeyNudgeLoginFlag,
  clearPasskeyNudgeLoginFlag,
} from '../app/webauthn';

/**
 * Module state survives React Strict Mode remounts and Framework remounts
 * across in-app navigation within the same page load.
 * @type {string|null|undefined}
 */
let nudgeLoginFlagCache;
/** @type {null|'show'|'hide'} */
let nudgeDecision = null;
/** @type {string} */
let nudgeNicknameCache = '';
/** @type {'prompt'|'creating'|'success'|'failure'} */
let nudgePhaseCache = 'prompt';

/**
 * Read the login-hop flag without clearing it (so refresh can re-show the nudge).
 * @return {string|null}
 */
function readNudgeLoginFlagCached() {
  if (nudgeLoginFlagCache !== undefined) {
    return nudgeLoginFlagCache;
  }
  const flag = readPasskeyNudgeLoginFlag();
  nudgeLoginFlagCache = flag;
  return flag;
}

/**
 * Clear pending/skip after explicit Not now, enroll, or passkey login.
 * Do not clear when the dialog is merely shown — CabView WebView reloads
 * wipe module state and need the pending cookie to re-show the nudge.
 */
function clearNudgeLoginFlagCached() {
  nudgeLoginFlagCache = null;
  clearPasskeyNudgeLoginFlag();
}

/**
 * Close the dialog and prevent remounts from reopening it.
 * Dismissed is explicit Not now (snooze). Enrolled means a passkey is present on this device.
 * @param {'dismissed'|'enrolled'} reason
 * @param {string|number} [uid]
 */
function finalizeNudge(reason, uid) {
  if (reason === 'enrolled') {
    markPasskeyPresentOnThisDevice(uid);
    nudgeLoginFlagCache = null;
  } else {
    setPasskeyNudgeDismissed(uid);
    clearNudgeLoginFlagCached();
  }
  nudgeDecision = 'hide';
  nudgePhaseCache = 'prompt';
}

/**
 * One-click passkey enrollment prompt after password login.
 * Mounted in Framework so it runs once on any post-login page (including CabView).
 * @return {JSX.Element}
 */
function PasskeyNudgeDialog() {
  const {t} = useTranslation();
  const userSettings = useSelector((state) => {
    return state.app.userSettings;
  });

  const [open, setOpen] = useState(nudgeDecision === 'show');
  const [nickname, setNickname] = useState(nudgeNicknameCache);
  const [phase, setPhase] = useState(nudgePhaseCache);
  const [notCreatedToast, setNotCreatedToast] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function evaluateNudge() {
      if (!PASSKEY_UI_ENABLED) {
        nudgeDecision = 'hide';
        return;
      }
      // Restore dialog after Strict Mode / Framework remount.
      if (nudgeDecision === 'show') {
        setNickname(nudgeNicknameCache);
        setPhase(nudgePhaseCache);
        setOpen(true);
        return;
      }
      if (nudgeDecision === 'hide') {
        return;
      }

      const uid = userSettings?.uid;
      const username = userSettings?.username;
      const databaseName = userSettings?.databaseName;
      if (uid == null || !username || !databaseName) {
        return;
      }

      const nudgeFlag = readNudgeLoginFlagCached();

      if (nudgeFlag === 'skip') {
        // Passkey login: this device already has a key for this account.
        finalizeNudge('enrolled', uid);
        return;
      }
      if (nudgeFlag !== 'pending') {
        // Cookie restore / in-app nav without a fresh password login
        // (pending was already consumed by Not now / enroll, or never set).
        nudgeDecision = 'hide';
        return;
      }

      if (!isWebAuthnAvailable()) {
        // Leave pending so a later refresh can retry if support appears.
        nudgeDecision = 'hide';
        return;
      }
      if (!(await isPlatformAuthenticatorAvailable())) {
        if (!cancelled) {
          nudgeDecision = 'hide';
        }
        return;
      }
      if (cancelled) {
        return;
      }
      if (isPasskeyNudgeDismissed(uid) || isPasskeyNudgeEnrolled(uid)) {
        clearNudgeLoginFlagCached();
        nudgeDecision = 'hide';
        return;
      }

      let existingNicknames = [];
      try {
        const listResult = await listPasskeys({
          sessionSyncUserName: username,
          sessionSyncDb: databaseName,
          fetchPostAuthSafe,
        });
        if (listResult.success) {
          existingNicknames = (listResult.credentials || []).map((c) => {
            return c.nickname;
          });
        }
      } catch (_e) {
        // Continue; nickname default still works with empty list.
      }
      if (cancelled) {
        return;
      }

      // Keep pending so CabView reload / full remount can re-show unless Not now.
      const defaultNickname = getDefaultPasskeyNickname(existingNicknames);
      nudgeNicknameCache = defaultNickname;
      nudgePhaseCache = 'prompt';
      nudgeDecision = 'show';
      setNickname(defaultNickname);
      setPhase('prompt');
      setOpen(true);
    }

    evaluateNudge();
    return () => {
      cancelled = true;
    };
  }, [userSettings?.uid, userSettings?.username, userSettings?.databaseName]);

  /**
   * Explicit Not now — snooze for this browser + account.
   */
  function handleNotNow() {
    if (phase === 'creating') {
      return;
    }
    finalizeNudge('dismissed', userSettings?.uid);
    setOpen(false);
    setPhase('prompt');
  }

  /**
   * Hide for this page load without snoozing (Close, backdrop, Escape).
   * Pending stays set so CabView reload can re-show unless the user clicked Not now.
   */
  function handleCloseResult() {
    nudgeDecision = 'hide';
    nudgePhaseCache = 'prompt';
    setOpen(false);
    setPhase('prompt');
  }

  /**
   * Leave the failure UI to open Personal Settings.
   */
  function handleGoToSettings() {
    handleCloseResult();
  }

  /**
   * Create started but did not store a passkey (cancel, Linux, verify fail, etc.).
   * Stay in the dialog for retry; do not mark enrolled or snooze.
   */
  function handleCreateFailed() {
    setNotCreatedToast(true);
    nudgePhaseCache = 'failure';
    setPhase('failure');
  }

  /**
   * Backdrop / Escape — hide for this page load without snoozing.
   * Cannot dismiss while creating.
   */
  function handleDialogDismiss() {
    if (phase === 'creating') {
      return;
    }
    handleCloseResult();
  }

  function handleTryAgain() {
    nudgePhaseCache = 'prompt';
    setPhase('prompt');
  }

  async function handleCreate() {
    if (phase === 'creating' || !userSettings?.username || !userSettings?.databaseName) {
      return;
    }
    nudgePhaseCache = 'creating';
    setPhase('creating');
    try {
      const result = await registerPasskey({
        sessionSyncUserName: userSettings.username,
        sessionSyncDb: userSettings.databaseName,
        nickname: nickname || undefined,
        fetchPostAuthSafe,
      });
      if (result.success || result.error === 'already_registered') {
        markPasskeyPresentOnThisDevice(userSettings.uid);
        nudgeLoginFlagCache = null;
        nudgePhaseCache = 'success';
        setPhase('success');
      } else {
        handleCreateFailed();
      }
    } catch (err) {
      console.error('Passkey nudge enroll failed:', err);
      handleCreateFailed();
    }
  }

  function dialogTitle() {
    if (phase === 'creating') {
      return t('Creating passkey…');
    }
    if (phase === 'success') {
      return t('Passkey created');
    }
    if (phase === 'failure') {
      return t('Passkey not created');
    }
    return t('Create a passkey?');
  }

  return (
    <>
      <Dialog open={open} onClose={handleDialogDismiss} maxWidth='xs' fullWidth>
        <DialogTitle>{dialogTitle()}</DialogTitle>
        <DialogContent>
          {phase === 'prompt' && (
            <p style={{marginTop: 0, marginBottom: 0}}>
              {t('Sign in faster next time with a passkey. You can also add one later in Personal Settings.')}
            </p>
          )}
          {phase === 'creating' && (
            <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', py: 2, gap: 2}}>
              <CircularProgress size={36} />
              <p style={{margin: 0, textAlign: 'center'}}>{t('Follow the prompts on your device…')}</p>
            </Box>
          )}
          {phase === 'success' && (
            <p style={{marginTop: 0}}>{t('Your passkey is ready. You can use it to sign in next time.')}</p>
          )}
          {phase === 'failure' && (
            <p style={{marginTop: 0}}>
              <Trans
                i18nKey='Could not create a passkey. Try again or use <settingsLink>Personal Settings</settingsLink>.'
                components={{
                  settingsLink: (
                    <Link
                      component={RouterLink}
                      to='/personal_settings'
                      underline='always'
                      onClick={handleGoToSettings}
                    />
                  ),
                }}
              />
            </p>
          )}
        </DialogContent>
        <DialogActions sx={{px: 3, pb: 2}}>
          {phase === 'prompt' && (
            <>
              <Button variant='ic-button' color='secondary' onClick={handleNotNow}>
                {t('Not now')}
              </Button>
              <Button variant='ic-button' color='primary' onClick={handleCreate}>
                {t('Create passkey')}
              </Button>
            </>
          )}
          {phase === 'success' && (
            <Button variant='ic-button' color='primary' onClick={handleCloseResult}>
              {t('Close')}
            </Button>
          )}
          {phase === 'failure' && (
            <>
              <Button variant='ic-button' color='secondary' onClick={handleTryAgain}>
                {t('Try Again')}
              </Button>
              <Button variant='ic-button' color='primary' onClick={handleCloseResult}>
                {t('Close')}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
      <Snackbar
        open={notCreatedToast}
        autoHideDuration={4000}
        onClose={() => {
          setNotCreatedToast(false);
        }}
        anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
      >
        <Alert
          severity='info'
          variant='filled'
          onClose={() => {
            setNotCreatedToast(false);
          }}
        >
          {t('Passkey not created')}
        </Alert>
      </Snackbar>
    </>
  );
}

export {PasskeyNudgeDialog};
